import React from 'react';
import { useTranslation } from 'react-i18next';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';
import { getProjects } from '../api';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import Autocomplete from '@mui/material/Autocomplete';

interface LeadFormProps {
  open: boolean;
  onClose: () => void;
  lead?: any;
  onSubmit: (data: any) => void;
}

export const LeadForm: React.FC<LeadFormProps> = ({ open, onClose, lead, onSubmit }) => {
  const { t } = useTranslation();
  const [projects, setProjects] = React.useState<any[]>([]);
  const [formData, setFormData] = React.useState({
    phone: lead?.phone || '',
    status: lead?.status || 'UNPROCESSED',
    manager_name: lead?.manager_name || '',
    customer_name: lead?.customer_name || '',
    project_id: lead?.project_id || '',
    project_name: lead?.project_name || '',
    note: lead?.note || '',
    comment: lead?.comment || '',
    bonus: lead?.bonus || false,
    price: lead?.price || '',
    address: lead?.address || ''
  });

  // Загружаем проекты при открытии формы
  React.useEffect(() => {
    const fetchProjects = async () => {
      try {
        const data = await getProjects();
        setProjects(data);
      } catch (error) {
        console.error('Ошибка при загрузке проектов:', error);
      }
    };

    if (open) {
      fetchProjects();
    }
  }, [open]);

  React.useEffect(() => {
    if (lead) {
      setFormData({
        phone: lead.phone || '',
        status: lead.status || 'UNPROCESSED',
        manager_name: lead.manager_name || '',
        customer_name: lead.customer_name || '',
        project_id: lead.project_id || '',
        project_name: lead.project_name || '',
        note: lead.note || '',
        comment: lead.comment || '',
        bonus: lead.bonus || false,
        price: lead.price || '',
        address: lead.address || ''
      });
    }
  }, [lead]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{lead ? t('edit') || 'Edit' : t('create') || 'Create'} {t('lead') || 'Lead'}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            sx={{ flex: 1 }}
            label={t('phone') || 'Phone'}
            value={formData.phone}
            onChange={e => setFormData({ ...formData, phone: e.target.value })}
            margin="normal"
            required
          />
          <TextField
            sx={{ flex: 1 }}
            select
            label={t('status') || 'Status'}
            value={formData.status}
            onChange={e => setFormData({ ...formData, status: e.target.value })}
            margin="normal"
          >
            <MenuItem value="UNPROCESSED">Необработанный</MenuItem>
            <MenuItem value="AT_WORK">В работе</MenuItem>
            <MenuItem value="COMPLETED">Завершен</MenuItem>
            <MenuItem value="NON_CALL">Недозвон</MenuItem>
            <MenuItem value="INVALID">Невалидный лид</MenuItem>
            <MenuItem value="BONUS">Бонусный</MenuItem>
          </TextField>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            sx={{ flex: 1 }}
            label={t('manager') || 'Manager'}
            value={formData.manager_name}
            onChange={e => setFormData({ ...formData, manager_name: e.target.value })}
            margin="normal"
          />
          <TextField
            sx={{ flex: 1 }}
            label="Клиент"
            value={formData.customer_name}
            onChange={e => setFormData({ ...formData, customer_name: e.target.value })}
            margin="normal"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            sx={{ flex: 1 }}
            label="Цена"
            type="number"
            value={formData.price}
            onChange={e => setFormData({ ...formData, price: e.target.value })}
            margin="normal"
            inputProps={{ min: 0, step: 1 }}
            placeholder="Введите цену"
          />
          <TextField
            sx={{ flex: 1 }}
            label="Адрес"
            value={formData.address}
            onChange={e => setFormData({ ...formData, address: e.target.value })}
            margin="normal"
            multiline
            rows={2}
            placeholder="Введите адрес"
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Autocomplete
            sx={{ flex: 0.3 }}
            options={projects}
            getOptionLabel={option => option.name}
            value={projects.find(p => String(p.id) === String(formData.project_id)) || null}
            onChange={(event, newValue) => {
              setFormData({
                ...formData,
                project_id: newValue ? newValue.id : '',
                project_name: newValue ? newValue.name : ''
              });
            }}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={params => (
              <TextField {...params} label="Проект" required margin="normal" placeholder="Найти проект..." />
            )}
          />
          <TextField
            sx={{ flex: 0.7 }}
            label={t('note') || 'Note'}
            value={formData.note}
            onChange={e => setFormData({ ...formData, note: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
        </Box>
        <Box sx={{ mt: 1 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.bonus}
                onChange={(e) => setFormData({ ...formData, bonus: e.target.checked })}
              />
            }
            label="Бонус"
          />
        </Box>
        <TextField
          fullWidth
          label="Комментарий"
          value={formData.comment}
          onChange={e => setFormData({ ...formData, comment: e.target.value })}
          margin="normal"
          multiline
          rows={6}
          sx={{ 
            '& .MuiInputBase-root': {
              height: '100%',
              '& textarea': {
                height: '100% !important',
                minHeight: '120px'
              }
            }
          }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('cancel') || 'Cancel'}</Button>
        <Button onClick={() => {
          // Валидация обязательных полей
          if (!formData.phone || !formData.phone.trim()) {
            return;
          }
          if (!formData.project_id) {
            // Требуем выбор проекта, чтобы не отправлять null в project_id
            alert('Пожалуйста, выберите проект');
            return;
          }

          // Маппинг полей формы в ожидаемые бекендом имена
          const payload = {
            projectId: parseInt(String(formData.project_id), 10),
            phone: formData.phone,
            status: formData.status,
            managerName: formData.manager_name || undefined,
            nameNote: formData.customer_name || undefined,
            note: formData.note || undefined,
            comment: formData.comment || undefined,
            bonus: !!formData.bonus,
            price: formData.price ? parseFloat(formData.price) : undefined,
            address: formData.address || undefined,
          };

          onSubmit(payload);
        }} variant="contained">{t('submit') || 'Submit'}</Button>
      </DialogActions>
    </Dialog>
  );
}; 